import { SearchIcon } from './Icons';
import React, { useState } from 'react';
import { InputBase, Stack, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Typography, designTokens } from '@platform-ui/design-system';
import { FOCUSABLE_ELEMENT_CLASS } from './Constants';

type InputProps = {
  value: string;
  onClickOrFocus: () => void;
  onChange: (e) => void;
  hintText: string;
  onKeyDown: (e) => void;
};

const ShortcutHintChip: React.FC<{ text: string }> = ({ text }) => {
  return (
    <Box
      sx={{
        borderRadius: '4px',
        border: '1px solid white',
        padding: '0 4px',
        textAlign: 'center',
      }}
    >
      <Typography variant="caption">{text}</Typography>
    </Box>
  );
};

const focusedBorder = `2px solid ${designTokens.colors.darkStandartInputLine}`;
const hoveredBorder = `1px solid ${designTokens.colors.darkOutlineBorder}`;
const border = `1px solid ${designTokens.colors.darkDividerLow}`;

export const SearchInput = React.forwardRef(
  (
    { value, onClickOrFocus, onChange, hintText, onKeyDown }: InputProps,
    ref,
  ) => {
    const { t } = useTranslation();
    const [focused, setFocused] = useState(false);
    return (
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{
          width: '300px',
          height: `calc(100% - ${focused ? '24px' : '22px'})`,
          margin: '10px 0',
          padding: `0 ${focused ? '15px' : '16px'}`,
          backgroundColor: designTokens.colors.darkContextMedium,
          color: 'white',
          borderRadius: '4px',
          border: focused ? focusedBorder : border,
          '&:hover': {
            border: focused ? focusedBorder : hoveredBorder,
          },
        }}
        onClick={onClickOrFocus}
      >
        <i style={{ cursor: 'pointer', display: 'flex' }}>
          <SearchIcon />
        </i>
        <InputBase
          sx={{ font: 'inherit' }}
          fullWidth
          placeholder={t('GlobalSearch.Search')}
          value={value}
          onChange={onChange}
          inputRef={ref}
          onFocus={() => {
            setFocused(true);
            onClickOrFocus();
          }}
          onBlur={() => setFocused(false)}
          onKeyDown={onKeyDown}
          inputProps={{
            'data-e2e': 'search-input',
            className: FOCUSABLE_ELEMENT_CLASS,
          }}
        />
        <ShortcutHintChip text={hintText} />
      </Stack>
    );
  },
);
